import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { OtpInput } from "./OtpInput";
import { storeSession, verifyMfa } from "../../shared/api/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MAX_ATTEMPTS = 5;
const LOCKOUT_SECONDS = 60;

export function MfaVerifyPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (lockedUntil) {
      const t = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(t);
    }
  }, [lockedUntil]);

  const remainingSec = lockedUntil ? Math.max(0, Math.ceil((lockedUntil - now) / 1000)) : 0;
  const isLocked = remainingSec > 0;

  useEffect(() => {
    if (lockedUntil && remainingSec === 0) {
      setLockedUntil(null);
      setAttempts(0);
      setError(null);
    }
  }, [lockedUntil, remainingSec]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (isLocked) return;
    setError(null);

    if (recoveryMode) {
      if (recoveryCode.replace(/\s+/g, "").length < 12) {
        setError("Mã khôi phục phải có ít nhất 12 ký tự.");
        return;
      }
    } else if (code.length !== 6) {
      setError("Vui lòng nhập đủ 6 số.");
      return;
    }

    const mfaToken = sessionStorage.getItem("addy.mfaToken");
    if (!mfaToken) {
      setError("Phiên xác thực đã hết hạn. Vui lòng đăng nhập lại.");
      return;
    }

    // TOTP (6 digits) and recovery codes both go through /mfa/verify.
    const submittedCode = recoveryMode ? recoveryCode.trim() : code;

    setSubmitting(true);
    try {
      const tokens = await verifyMfa(mfaToken, submittedCode);
      sessionStorage.removeItem("addy.mfaToken");
      storeSession(tokens);
      navigate("/dashboard");
      return;
    } catch {
      const nextAttempts = attempts + 1;
      setAttempts(nextAttempts);
      setCode("");
      if (nextAttempts >= MAX_ATTEMPTS) {
        setLockedUntil(Date.now() + LOCKOUT_SECONDS * 1000);
        setError(`Khoá tạm thời sau ${MAX_ATTEMPTS} lần sai.`);
      } else {
        setError(
          `Mã không đúng. Còn ${MAX_ATTEMPTS - nextAttempts} lần thử trước khi bị khoá tạm.`
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4 font-sans">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 text-3xl font-extrabold tracking-tight text-primary">
            <span aria-hidden="true">✚</span> Addy HIS
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Hệ thống quản lý phòng khám
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Xác minh 2 lớp</CardTitle>
            <CardDescription>
              {recoveryMode
                ? "Nhập một mã khôi phục đã lưu khi thiết lập MFA."
                : "Nhập mã 6 số đang hiển thị trên ứng dụng xác thực."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={onSubmit} noValidate>
              {!recoveryMode && (
                <div className="space-y-2">
                  <Label>Mã xác thực</Label>
                  <OtpInput
                    value={code}
                    onChange={setCode}
                    disabled={submitting || isLocked}
                    hasError={!!error}
                  />
                  <p className="text-xs text-muted-foreground">
                    Mẹo: dùng phím{" "}
                    <kbd className="rounded border border-input bg-muted px-1 font-mono text-[0.7rem]">
                      ⌘V
                    </kbd>{" "}
                    /{" "}
                    <kbd className="rounded border border-input bg-muted px-1 font-mono text-[0.7rem]">
                      Ctrl+V
                    </kbd>{" "}
                    để dán cả 6 số.
                  </p>
                </div>
              )}

              {recoveryMode && (
                <div className="space-y-2">
                  <Label htmlFor="recovery">Mã khôi phục</Label>
                  <Input
                    id="recovery"
                    type="text"
                    autoComplete="one-time-code"
                    autoFocus
                    placeholder="A1B2-C3D4-E5F6"
                    className="font-mono"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                    disabled={submitting || isLocked}
                    aria-invalid={Boolean(error)}
                  />
                </div>
              )}

              {error && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {isLocked ? `Khoá tạm thời — thử lại sau ${remainingSec}s.` : error}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={submitting || isLocked}>
                {submitting
                  ? "Đang xác thực…"
                  : isLocked
                  ? `Đợi ${remainingSec}s`
                  : "Xác minh"}
              </Button>

              <div className="text-center text-sm">
                {recoveryMode ? (
                  <button
                    type="button"
                    className="font-medium text-primary hover:underline"
                    onClick={() => {
                      setRecoveryMode(false);
                      setError(null);
                    }}
                  >
                    ← Quay lại nhập mã từ ứng dụng
                  </button>
                ) : (
                  <button
                    type="button"
                    className="font-medium text-primary hover:underline"
                    onClick={() => {
                      setRecoveryMode(true);
                      setError(null);
                    }}
                  >
                    Mất điện thoại? Dùng mã khôi phục
                  </button>
                )}
              </div>

              <p className="text-center text-sm text-muted-foreground">
                Hoặc{" "}
                <Link to="/login" className="font-medium text-primary hover:underline">
                  đăng nhập với tài khoản khác
                </Link>
                .
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
